<template>
  <WizardZoneSelectStep
    v-if="wizardStep === 1"
    :step="wizardStep"
    :zones="zones"
    :selectedZone="selectedZone"
    :handleChange="onSelectZoneChange"
  />
  <WizardConnectStep v-if="wizardStep === 2" :step="wizardStep" />
  <WizardSettingStep
    v-if="wizardStep === 3"
    :step="wizardStep"
    :controller="connectedController"
    :ssid="settingParams.ssid"
    :password="settingParams.pwd"
    :handleSsidChange="onSsidChange"
    :handlePasswordChange="onPasswordChange"
  />
  <WizardSetWifiCompleteStep
    v-if="wizardStep === 4"
    :step="wizardStep"
    :controller="connectedController"
  />

  <div id="footer" class="app-footer ms-0">
    <footer class="mt-auto">
      <div class="row">
        <div class="col" />
        <div class="col" />
        <div class="col">
          <button
            type="button"
            class="btn btn-primary btn-lg"
            style="width: 100%"
            @click="onNextStepClick"
          >
            {{
              wizardStep === 1
                ? '선택'
                : wizardStep === 2
                ? '연결'
                : wizardStep === 3
                ? '설정'
                : '완료'
            }}
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, getCurrentInstance, computed, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import useMobile from '@/common/hooks/useMobile';
import useControllerProvisioning from '@/common/hooks/useControllerProvisioning';
import WizardZoneSelectStep from './components/wizard/WizardZoneSelectStep';
import WizardConnectStep from './components/wizard/WizardConnectStep';
import WizardSettingStep from './components/wizard/WizardSettingStep';
import WizardSetWifiCompleteStep from './components/wizard/WizardSetWifiCompleteStep';
import { SettingConfig } from '@/common/constants';

const store = useStore();
const router = useRouter();
const {
  getWifiInfo,
  requestRegistController,
  requestTransferController,
  disConnectWifi,
  connectBleDevice,
} = useMobile();
const {
  connectWifiAp,
  provisionOverWifiAp,
  checkBleAdapter,
  scanBleWithRetry,
  disconnectBleSafe,
  provisionOverBle,
  normalizeControllerInfoFromSerial,
} = useControllerProvisioning();
const { emitter, mobile, logger } =
  getCurrentInstance().appContext.config.globalProperties;
const zones = computed(() => store.getters['home/getZones']);
const account = computed(() => store.getters['account']);
const selectedZone = ref(undefined);
let requestCount = 0;
let isSettingController = false;

// BLE 관련 상태
const connectionType = ref(null); // 'ble' | 'wifi' | null
const isBleConnected = ref(false);

const settingParams = ref({
  ssid: '',
  pwd: '',
  firmware_version: '',
  firmware_url: '',
  uuid: '',
  device_key: '',
  mqtt_url: process.env.VUE_APP_CTRL_MQTT_DOMAIN,
  mqtt_username: '',
  mqtt_password: '',
});

const connectedController = ref({
  _id: '',
  version: '',
  model: '',
});
const wizardStep = ref(1);

onBeforeUnmount(() => {
  disConnectWifi();
  disconnectBleSafe().catch(() => {});
});

const showPopAlert = (msg) => {
  emitter.emit('showPopAlert', {
    description: msg,
  });
};

const onSsidChange = (value) => {
  settingParams.value.ssid = value;
};

const onPasswordChange = (value) => {
  settingParams.value.pwd = value;
};

const onSelectZoneChange = (e) => {
  const zone = zones.value.find((zone) => zone._id == e.target.value);
  selectedZone.value = zone;
};

// WiFi 연결 함수 (BLE 실패 시 사용)
const connectWithWifi = async () => {
  try {
    emitter.emit('showLoading');
    emitter.emit('setLoadingText', '조명제어기에 연결하고 있습니다.');
    
    const { controllerInfo } = await connectWifiAp();
    connectedController.value.model = controllerInfo.model;
    connectedController.value._id = controllerInfo._id;
    connectedController.value.version = controllerInfo.version;
    
    connectionType.value = 'wifi';
    isBleConnected.value = false;
    emitter.emit('hideLoading');
    wizardStep.value += 1;
  } catch (e) {
    emitter.emit('hideLoading');
    showPopAlert(e?.message || e?.toString() || 'WiFi 연결에 실패했습니다.');
  }
};

const onNextStepClick = async () => {
  try {
    if (wizardStep.value === 1) {
      if (!selectedZone.value) {
        showPopAlert('컨트롤러를 등록할 영역을 선택해주새요.');
        return;
      }
    } else if (wizardStep.value === 2) {
      // BLE 스캔 시도 (최대 2회)
      connectionType.value = null;
      isBleConnected.value = false;

      try {
        // 1. BLE 어댑터 상태 확인
        emitter.emit('showLoading');
        emitter.emit('setLoadingText', 'BLE 상태 확인 중...');

        try {
          await checkBleAdapter();
        } catch (e) {
          // BLE 사용 불가능한 경우 즉시 WiFi로 전환 제안
          emitter.emit('hideLoading');
          emitter.emit('showPopConfirm', {
            description: `${e?.message || e}\n\nWiFi 연결로 전환하시겠습니까?`,
            okCallback: async () => {
              await connectWithWifi();
            },
            cancelCallback: () => {
              // 취소 시 아무것도 하지 않음
            },
          });
          return;
        }

        // 2. BLE 스캔 시도 (최대 2회)
        emitter.emit('setLoadingText', 'BLE 장치 스캔 중...');
        let devices = [];
        try {
          devices = await scanBleWithRetry({ maxRetries: 0, timeout: 5, filterPrefix: 'inshow_m' });
          logger.log('BLE devices found:', devices);
        } catch (e) {
          // 스캔 2회 실패 시 WiFi 전환 확인
          logger.log('BLE scan failed after retries:', e);
          emitter.emit('hideLoading');
          emitter.emit('showPopConfirm', {
            description: 'BLE 장치를 찾을 수 없습니다.\n\nWiFi 연결로 전환하시겠습니까?',
            okCallback: async () => {
              await connectWithWifi();
            },
            cancelCallback: () => {
              // 취소 시 아무것도 하지 않음
            },
          });
          return;
        }

        if (devices.length === 0) {
          // 장치를 찾지 못한 경우 WiFi 전환 확인
          emitter.emit('hideLoading');
          emitter.emit('showPopConfirm', {
            description: 'BLE 장치를 찾을 수 없습니다.\n\nWiFi 연결로 전환하시겠습니까?',
            okCallback: async () => {
              await connectWithWifi();
            },
            cancelCallback: () => {
              // 취소 시 아무것도 하지 않음
            },
          });
          return;
        }

        // 3. BLE 연결 시도
        // Flutter 앱에서 스캔 완전 종료를 보장하므로, 웹에서는 추가 안정화 시간만 확보
        // 하드웨어 개발자 요구사항: 스캔이 완전히 끝난 후에 연결해야 실패 확률이 낮아짐
        await new Promise((r) => setTimeout(r, 2000)); // 스캔 후 안정화 대기
        const device = devices[0];
        emitter.emit('setLoadingText', `BLE 연결 중: ${device.name || '장치'}`);

        try {
          await connectBleDevice(device.deviceId, null, device.name);
          logger.log('BLE connected successfully');
          
          // 장치 이름에서 직접 정보 추출 (getDeviceInfo 호출 불필요)
          // device.name 형식: "InShow_M24_885721b27bc0" -> 시리얼: "M24_885721b27bc0"
          let serialNo = device.name || '';
          if (serialNo.startsWith('InShow_')) {
            serialNo = serialNo.substring('InShow_'.length);
          }
          
          if (serialNo) {
            const controllerInfo = normalizeControllerInfoFromSerial(
              serialNo,
              '0', // BLE 장치는 버전 정보가 없으므로 기본값 사용
            );
            connectedController.value.model = controllerInfo.model;
            connectedController.value._id = controllerInfo._id;
            connectedController.value.version = controllerInfo.version;
            
            connectionType.value = 'ble';
            isBleConnected.value = true;
            emitter.emit('hideLoading');
            wizardStep.value += 1;
            return;
          } else {
            throw new Error('BLE 장치 이름에서 정보를 추출할 수 없습니다.');
          }
        } catch (e) {
          logger.log('BLE connection failed:', e);
          await disconnectBleSafe();
          emitter.emit('hideLoading');
          
          // BLE 연결 실패 시 WiFi 전환 확인
          emitter.emit('showPopConfirm', {
            description: `BLE 연결에 실패했습니다.\n\n오류: ${e?.message || e}\n\nWiFi 연결로 전환하시겠습니까?`,
            okCallback: async () => {
              await connectWithWifi();
            },
            cancelCallback: () => {
              // 취소 시 아무것도 하지 않음
            },
          });
          return;
        }
      } catch (e) {
        logger.log('Connection process failed:', e);
        emitter.emit('hideLoading');
        showPopAlert(e?.message || e?.toString() || '연결에 실패했습니다.');
      }
    } else if (wizardStep.value === 3) {
      if (!isSettingController) {
        if (settingParams.value.ssid.length === 0) {
          showPopAlert('SSID를 입력해주세요.');
          return;
        }
        if (settingParams.value.pwd.length === 0) {
          showPopAlert('비밀번호를 입력해주세요.');
          return;
        }

        // connectionType에 따라 프로비저닝 방식 선택
        if (connectionType.value === 'ble' && isBleConnected.value) {
          emitter.emit('showLoading');
          emitter.emit('setLoadingText', 'BLE로 WiFi/MQTT 설정을 전송합니다.');
          
          try {
            const provisionResult = await provisionOverBle({
              wifi: {
                ssid: settingParams.value.ssid,
                password: settingParams.value.pwd,
              },
              mqtt: {
                url: settingParams.value.mqtt_url,
                username: settingParams.value.mqtt_username,
                password: settingParams.value.mqtt_password,
              },
              wifiTimeout: 15,
              mqttTimeout: 15,
            });

            logger.log('BLE provision result:', provisionResult);

            if (
              !provisionResult?.wifi ||
              (!provisionResult.wifi.wifi_state && !provisionResult.wifi.connected)
            ) {
              throw new Error('WiFi 연결 실패');
            }

            await disconnectBleSafe();
            isBleConnected.value = false;
            emitter.emit('hideLoading');
          } catch (e) {
            emitter.emit('hideLoading');
            throw e;
          }
        } else {
          await provisionOverWifiAp({
            settingParams: settingParams.value,
            apWifi: undefined,
          });
        }
        isSettingController = true;
      }

      // await listenChangedNetworkInfo();

      const request = () => {
        return new Promise(async (resolve, reject) => {
          setTimeout(async () => {
            try {
              const result = await requestRegistController(
                connectedController.value,
                selectedZone.value._id,
              );
              resolve(result);
            } catch (e) {
              resolve(undefined);
            }
          }, SettingConfig.requestInterval);
        });
      };

      emitter.emit('showLoading');
      let result = undefined;
      for (let i = 0; i < SettingConfig.requestCount; i++) {
        result = await request();
        if (result?.resultCode === "0000") {
          await store.dispatch('home/fetchAllZones');

          emitter.emit('hideLoading');
          wizardStep.value += 1;
          return;
        }
        if (result?.resultCode === "1001") {
          emitter.emit('hideLoading');

          const transferController = async () => {
            const transResult = await requestTransferController(
              connectedController.value,
              account.value._id,
              result.resultData._id,
              selectedZone.value._id
            );

            if (transResult?.resultCode === "0000") {
              wizardStep.value += 1;
              // emitter.emit("showPopAlert", {
              //   description: '이관이 완료되었습니다.',
              //   okCallback: () => router.go(-1),
              //   cancelCallback: () => router.go(-1),
              // });
            } else {
              emitter.emit("showPopAlert", {
                description: '이관에 실패했습니다.'
              });
            }
          }

          emitter.emit('showPopAlert', {
            title: "등록된 컨트롤러",
            description: "이미 등록된 컨트롤러입니다.\n이관 처리됩니다.",
            okCallback: transferController,
            cancelCallback: transferController
          });

          return;
        }
      }
 
      showPopAlert(result?.resultMessage?.length > 0 ? result.resultMessage : "컨트롤러 등록에 실패했습니다.");
      emitter.emit('hideLoading');
      return;
    } else if (wizardStep.value === 4) {
      emitter.emit('hideLoading');
      router.go(-1);
      return;
    }

    wizardStep.value += 1;
  } catch (err) {
    console.log(err);
    if (err?.length > 0) {
      showPopAlert(err);
    }
  }
};
</script>
